import React from 'react';

/**
 * Componente Loader/Spinner para indicar carga
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string} props.size - Tamaño del spinner ('sm', 'md', 'lg', 'xl')
 * @param {string} props.color - Color del spinner ('primary', 'secondary', 'white')
 * @param {string} props.text - Texto opcional para mostrar debajo del spinner
 * @param {string} props.className - Clases adicionales
 * @param {boolean} props.fullScreen - Si el spinner debe ocupar toda la pantalla
 * @returns {JSX.Element} - Componente Loader
 */
const Loader = ({
  size = 'md',
  color = 'primary',
  text = '',
  className = '',
  fullScreen = false,
  ...rest
}) => {
  // Mapeo de tamaños a clases
  const sizeClasses = {
    sm: 'h-4 w-4 border-2',
    md: 'h-8 w-8 border-2',
    lg: 'h-12 w-12 border-3',
    xl: 'h-16 w-16 border-4',
  };

  // Mapeo de colores
  const colorClasses = {
    primary: 'border-blue-500',
    secondary: 'border-gray-500',
    white: 'border-white',
    success: 'border-green-500',
    warning: 'border-yellow-500',
    danger: 'border-red-500',
  };

  const spinnerSize = sizeClasses[size] || sizeClasses.md;
  const spinnerColor = colorClasses[color] || colorClasses.primary;

  // Contenedor condicional para fullScreen
  const Container = ({ children }) => {
    return fullScreen ? (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
        {children}
      </div>
    ) : (
      <>{children}</>
    );
  };

  return (
    <Container>
      <div className={`flex flex-col items-center justify-center ${className}`} {...rest}>
        <div 
          className={`rounded-full border-t-transparent ${spinnerSize} ${spinnerColor} animate-spin`}
        />
        {text && (
          <span className="mt-2 text-sm text-gray-300">{text}</span>
        )}
      </div>
    </Container>
  );
};

/**
 * Componente para un área de carga con spinner centrado
 * 
 * @param {Object} props - Propiedades del componente
 * @param {ReactNode} props.children - Contenido alternativo si no está cargando
 * @param {boolean} props.loading - Si está en estado de carga
 * @param {string} props.text - Texto para mostrar durante la carga
 * @param {Object} props.loaderProps - Propiedades adicionales para el componente Loader
 * @returns {JSX.Element} - Componente LoadingArea
 */
export const LoadingArea = ({
  children,
  loading = false,
  text = 'Cargando...',
  loaderProps = {},
  ...rest
}) => {
  if (loading) {
    return (
      <div className="min-h-[200px] flex items-center justify-center" {...rest}>
        <Loader text={text} {...loaderProps} />
      </div>
    );
  }

  return <>{children}</>;
};

export default Loader;